// libraries.
import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useFormContext } from "react-hook-form";

// hooks.
import { useGetLeadDetailInfo } from "@store/apiSlices/leadDetail/leadDetail.hooks";
import { useGetInventoryPropertyInfo } from "@store/apiSlices/inventoryPropertyInfo/inventoryPropertyInfo.hook";
import { useApplyRangeToPublicationPrice } from "@pages/InventoryPage/modules/InventorySummary/components/shared/hooks/applyRangeToPublicationPrice";
import {
  getSelectedOption,
  getSuggestionOption,
} from "@domains/VIM/slices/vim.slice";

// utils.
import { MinPriceFooter } from "@pages/InventoryPage/modules/InventorySummary/components/shared/MinPriceFooter";
import {
  calculatePercentageInPrice,
  percentageToApplyInRelatedPricesCO,
} from "@shared/DashboardComponentsSummary/components/utils";

export const MinPublicationPrice = () => {
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();
  const minPublicationPriceInput = watch("minPublicationPrice");
  const publicationPrice = watch("publicationPrice");
  const applyPercentageToPrices = watch("applyPercentageToPrices");
  const leader = watch("leader");

  const { applyRangeToPublicationPrice } = useApplyRangeToPublicationPrice();

  const { area } = useGetLeadDetailInfo();

  const isEditing = useRef(false);
  const selectedOption = useSelector(getSelectedOption);
  const suggestionOption = useSelector(getSuggestionOption);

  const { minPublicationPrice: defaultMinPublicationPrice } =
    useGetInventoryPropertyInfo();

  const minPublicationPriceM2 =
    Number(minPublicationPriceInput ?? defaultMinPublicationPrice) / area;

  useEffect(() => {
    if (!isEditing.current && defaultMinPublicationPrice) {
      setValue("minPublicationPrice", defaultMinPublicationPrice);
    }
  }, [defaultMinPublicationPrice, setValue]);

  const isDisabled = Boolean(selectedOption || suggestionOption);

  useEffect(() => {
    if (publicationPrice && applyPercentageToPrices && !isEditing.current) {
      const priceMin = calculatePercentageInPrice(
        publicationPrice,
        false,
        percentageToApplyInRelatedPricesCO,
      );
      setValue("minPublicationPrice", priceMin, {
        shouldDirty: true,
      });
    }
    isEditing.current = false;
  }, [publicationPrice, applyPercentageToPrices, setValue]);

  return (
    <>
      <div className="d-flex justify-content-between align-items-center">
        <div>Precio mínimo publicado</div>
        <input
          data-testid="minPublicationPriceInput"
          disabled={isDisabled}
          {...register("minPublicationPrice", {
            onChange: (e) => {
              isEditing.current = true;
              if (applyPercentageToPrices) {
                const price = calculatePercentageInPrice(
                  e.target.value,
                  true,
                  percentageToApplyInRelatedPricesCO,
                );
                setValue("publicationPrice", price);
              }
            },
            validate: (value) =>
              applyRangeToPublicationPrice({
                defaultPrice: defaultMinPublicationPrice,
                leader,
                price: value,
              }),
            valueAsNumber: true,
          })}
          type="text"
          className="rounded-3 p-2"
        />
      </div>
      <MinPriceFooter pricePerM2={minPublicationPriceM2} errors={errors} />
    </>
  );
};
